import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePageSeo } from "@/lib/usePageSeo";
import { useJsonLd, buildLocalBusinessSchema } from "@/lib/seoSchema";
import { Hero } from "@/components/home/Hero";
import { ServicesSection } from "@/components/home/ServicesSection";
import { FeaturedProjects } from "@/components/home/FeaturedProjects";
import { TrustedBrands } from "@/components/home/TrustedBrands";
import { LaptopSetups } from "@/components/home/LaptopSetups";
import { SoftwareSection } from "@/components/home/SoftwareSection";
// Testimonial section intentionally omitted from the homepage rendering
// (per commercial direction). The `Testimonial` component, its CMS fields,
// admin editor, and backend routes are preserved so it can be re-enabled
// later without a rebuild.
import { FinalCTA } from "@/components/home/FinalCTA";
import { KineticStatement } from "@/components/home/KineticStatement";
import { CredibilityStatement } from "@/components/home/CredibilityStatement";

// ─── Feb 2026 architectural correction ────────────────────────────
// The previous implementation wrapped six independent API calls in a
// Promise.all and gated the entire homepage — including the Navbar,
// Hero and everything beneath — behind a full-viewport BrandedLoader
// until all six resolved. That made the SLOWEST of /services,
// /portfolio, /laptops, /software, /brands, /settings the gating
// factor for first paint. A slow /portfolio delayed the Navbar
// mount; any single failure replaced the whole page with a loader.
//
// This module now:
//   1. Renders the page shell (including Navbar + Hero) on the very
//      first render, with no page-level loader in front.
//   2. Fires the six endpoints independently. Each section receives
//      its own data slice via props; sections render immediately
//      (with sensible empty/default state) while their fetch is
//      still in flight.
//   3. Isolates failures — a rejected /portfolio only leaves the
//      Portfolio grid empty; every other section keeps rendering.
//   4. Seeds `settings` with the CMS-known Hero media URLs so the
//      video path activates on the very first render. When
//      /settings resolves, the same URLs produce no DOM diff — no
//      flash, no layout shift.
//
// BrandedLoader is intentionally NOT imported here — the component
// itself is preserved for legitimate per-page use in configurator /
// detail pages.

// Canonical Hero snapshot at the time of this change. Hero media
// URLs point at the frontend's static `public/brand/` folder (Feb
// 2026 mobile-latency audit): serving these first-paint assets via
// the same static webserver that delivers the JS bundle bypasses
// the FastAPI `/api/uploads/` route, which was showing 200-4000 ms
// TTFB variance on mobile cold loads under backend contention.
// Everything else matches the current /api/settings response so the
// Hero renders its final visual on the very first React commit. If
// an admin later replaces the logo/hero via the CMS dashboard, the
// /settings fetch below merges the live URLs on top.
//
// Sep 2026 Website Repositioning: the seed mirrors the repositioned
// creative-studio copy and carries the public-commerce flag OFF so the
// LaptopSetups/SoftwareSection blocks never flash on first paint.
const INITIAL_SETTINGS = {
  // Hero media (static, same-origin, no Python in the request path)
  hero_video_enabled: true,
  hero_video_mp4: "/brand/hero.mp4",
  hero_image_desktop: "/brand/hero-desktop.webp",
  hero_image_mobile: "/brand/hero-mobile.webp",
  // Hero copy
  hero_eyebrow: "Brand Studio · Since 2005",
  hero_title_line1: "Brands Built for",
  hero_title_line2: "the Real World.",
  hero_description:
    "Branding, design, campaigns, packaging and 3D visualization — from complete brands to everyday design. Made in Aruba, for businesses anywhere.",
  hero_cta_primary_label: "Start a Project",
  hero_cta_primary_link: "/contact",
  hero_cta_secondary_label: "Order Design Online",
  hero_cta_secondary_link: "/services#services-finder",
  // Website Repositioning master flag — OFF in the seed so the
  // workstation/software sections never render before /settings resolves.
  public_commerce_workstations_software_enabled: false,
};

export default function HomePage() {
  // Each below-the-fold section owns its own data slice. Empty
  // initial values render an empty section (no error, no loader).
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [laptops, setLaptops] = useState([]);
  const [software, setSoftware] = useState([]);
  const [brands, setBrands] = useState([]);
  // Hero seed values so the video path is active from first render.
  const [settings, setSettings] = useState(INITIAL_SETTINGS);
  // Tracks the CMS public-commerce flag as soon as /settings resolves so
  // refetches (visibilitychange/pageshow) skip the retired product feeds
  // while the flag is OFF (Website Repositioning, Sep 2026).
  const commerceRef = React.useRef(
    INITIAL_SETTINGS.public_commerce_workstations_software_enabled === true
  );

  usePageSeo({
    title: "Ur Designs — Brand & Creative Studio in Aruba Since 2005",
    description:
      "Brand strategy, identity, packaging, campaigns and 3D visualization for businesses in Aruba and beyond. Built by Ur Designs since 2005.",
    path: "/",
    image: settings.hero_image_desktop || undefined,
  });
  useJsonLd(buildLocalBusinessSchema(settings));

  // Workstation + software product feeds — only fetched while the CMS
  // public-commerce flag is ON. The endpoints return genuine HTTP 410
  // while OFF, so fetching them would be wasted requests.
  const fetchProductSections = React.useCallback(() => {
    api.get("/laptops").then((r) => setLaptops(r.data)).catch(() => {});
    api.get("/software").then((r) => setSoftware(r.data)).catch(() => {});
  }, []);

  const fetchAll = React.useCallback(() => {
    // Independent fetches. `.catch(() => {})` intentionally
    // swallows the error so a slow or failing endpoint cannot
    // poison a sibling section. Every section component already
    // handles empty arrays / empty settings via its own defaults.
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    if (commerceRef.current) fetchProductSections();
    // Merge live CMS values over the seed so any admin-only fields
    // (services_home_heading, brands_eyebrow, etc.) fill in without
    // overwriting the seeded Hero URLs if for some reason /settings
    // omits them.
    api
      .get("/settings")
      .then((r) => {
        setSettings((prev) => ({ ...prev, ...r.data }));
        // Flag just turned ON after mount → fetch the product sections
        // once so the homepage reactivates without a reload.
        if (
          r.data?.public_commerce_workstations_software_enabled === true &&
          !commerceRef.current
        ) {
          commerceRef.current = true;
          fetchProductSections();
        }
      })
      .catch(() => {});
  }, [fetchProductSections]);

  useEffect(() => {
    fetchAll();
    // Re-fetch when the user tabs back or the page is restored from
    // bfcache so reorders / edits done in another tab are picked up
    // without a manual reload.
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchAll();
    };
    const onPageShow = (e) => {
      if (e.persisted) fetchAll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [fetchAll]);

  // Website Repositioning (Sep 2026) — the LaptopSetups + SoftwareSection
  // homepage blocks render ONLY while the CMS public-commerce flag is ON.
  // The components, their CMS content and the section order are preserved
  // untouched for reactivation.
  const commerceEnabled =
    settings.public_commerce_workstations_software_enabled === true;

  return (
    <div data-testid="home-page">
      <Hero settings={settings} />
      <TrustedBrands brands={brands} eyebrow={settings.brands_eyebrow} />
      <ServicesSection
        services={services}
        showDot={!!settings.services_home_show_dot}
        eyebrow={settings.services_home_eyebrow}
        heading={settings.services_home_heading}
        headingAccent={settings.services_home_heading_accent}
        accentInline={!!settings.services_home_accent_inline}
        description={settings.services_home_description}
      />
      <KineticStatement />
      <FeaturedProjects
        projects={(portfolio || []).filter((p) => p && p.featured === true)}
        showDot={!!settings.featured_projects_show_dot}
      />
      <CredibilityStatement />
      {commerceEnabled && <LaptopSetups laptops={laptops} />}
      {commerceEnabled && <SoftwareSection software={software} />}
      <FinalCTA />
    </div>
  );
}
